// apps/web/src/pages/vendas/detalhe/gerar-op-dialog.tsx — Fase 12, Plano 29 (NFE-06, FECH-06, D-10, T-12-36).
// "Gerar OP" de um lote recebido sem OP (item sem roteiro vigente na conferência): confirma o roteiro (pré-preenchido
// com o do item; `RoteiroCombobox` quando vazio) e chama POST /vendas/pedidos/lotes/:loteId/gerar-op (pv:ajustar).
// A OP nasce RASCUNHO (Fila de entrada do Kanban); a autorização real fica no servidor.
import { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { FormMessage } from '../../../components/ui/form-message';
import { Label } from '../../../components/ui/label';
import { RoteiroCombobox } from '../../../components/vendas/roteiro-combobox';
import { apiMutate } from '../../../lib/csrf-fetch';
import { atalhoDoErro, mensagemDoBloqueio, type AtalhoDaTela } from '../../../lib/roteiros/cadastro-peca';
import { lerErroApi, MENSAGEM_FALHA_PV } from '../../../lib/vendas/erro-api';

interface GerarOpDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly loteId: string;
  readonly loteInternoPieltec: string;
  /** Roteiro já definido no item do PV (pré-preenchido); null quando o item não tem roteiro. */
  readonly roteiroIdInicial: string | null;
  /** Chamado após a OP gerada: a tela recarrega o PV (a pendência "Sem roteiro" some). */
  readonly onGerada: () => void;
}

/** D-25c: o bloqueio por produto sem cadastro ou peça sem roteiro vira mensagem em português com o atalho para resolver. */
async function falhaDaGeracao(res: Response): Promise<{ mensagem: string; atalho: AtalhoDaTela | null }> {
  const erro = await lerErroApi(res, MENSAGEM_FALHA_PV);
  return { mensagem: mensagemDoBloqueio(erro.codigo, erro.mensagem), atalho: atalhoDoErro(erro) };
}

export function GerarOpDialog({ aberto, onAbertoChange, loteId, loteInternoPieltec, roteiroIdInicial, onGerada }: GerarOpDialogProps) {
  const [roteiro, setRoteiro] = useState<string | null>(roteiroIdInicial);
  const [erro, setErro] = useState<string | null>(null);
  const [atalho, setAtalho] = useState<AtalhoDaTela | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setRoteiro(roteiroIdInicial);
    setErro(null);
    setAtalho(null);
  }, [aberto, roteiroIdInicial]);

  async function gerar(): Promise<void> {
    // Sem roteiro escolhido o corpo vai vazio: o servidor usa o roteiro da peça ou devolve o bloqueio com o atalho (D-25c).
    setErro(null);
    setAtalho(null);
    setEnviando(true);
    try {
      const res = await apiMutate(`/api/v1/vendas/pedidos/lotes/${encodeURIComponent(loteId)}/gerar-op`, { method: 'POST', body: roteiro ? { roteiroId: roteiro } : {} });
      if (!res.ok) {
        const falha = await falhaDaGeracao(res);
        setErro(falha.mensagem);
        setAtalho(falha.atalho);
        return;
      }
      onAbertoChange(false);
      onGerada();
    } catch {
      setErro(MENSAGEM_FALHA_PV);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={onAbertoChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Gerar OP do lote {loteInternoPieltec}</DialogTitle>
          <DialogDescription>A OP é criada na Fila de entrada do Kanban, com o roteiro vigente escolhido abaixo.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Label htmlFor={`gerar-op-roteiro-${loteId}`}>Processo / roteiro (opcional)</Label>
          <p className="text-xs text-muted-foreground">Em branco, a OP usa o roteiro da peça.</p>
          <RoteiroCombobox id={`gerar-op-roteiro-${loteId}`} value={roteiro} onChange={setRoteiro} disabled={enviando} />
        </div>

        {erro && (
          <div role="alert">
            <FormMessage>{erro}</FormMessage>
            {atalho && (
              <a className="mt-1 inline-block text-sm font-semibold text-blue-700 underline underline-offset-4" href={atalho.href}>
                {atalho.rotulo}
              </a>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={enviando}>
            Voltar
          </Button>
          <Button type="button" onClick={() => void gerar()} disabled={enviando}>
            {enviando ? 'Gerando...' : 'Gerar OP'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
